import React, { useEffect, useMemo, useRef, useState } from "react";
import TableColumn from "../../components/TableColumn/TableColumn";
import api from "../../services/api";
import "./JoinRequests.scss";
import { format } from "date-fns";
import SideBar from "../../components/SideBar/SideBar";
import TopNav from "../../components/TopNav/TopNav";
import profileDefault from "../../assets/images/profileDefault.png";
import { useBadgeCounts } from "../../contexts/BadgeCountsContext";
import { isPriority, matchesSearch, referralLine, socialLinks, verificationLabel } from "./joinRequestHelpers";

// Members-only waitlist (board #398). Invited or referred requests come first, then verified.
// Approve lets them in and pushes "You're in, <first name>". Declined people never see a decline.
const TABS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "declined", label: "Declined" },
];
const RECORDS = 25;

// Loads a link as an image in this browser before it goes live in the app; gives up after 10s.
const opensAsImage = (url) => new Promise((resolve) => {
  const img = new Image();
  const timer = setTimeout(() => resolve(false), 10000);
  img.onload = () => { clearTimeout(timer); resolve(true); };
  img.onerror = () => { clearTimeout(timer); resolve(false); };
  img.src = url;
});

const JoinRequests = () => {
  const { refreshCounts } = useBadgeCounts();
  const [status, setStatus] = useState("pending");
  const [data, setData] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, declined: 0 });
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [busyId, setBusyId] = useState(null);
  const latestLoad = useRef(0);
  // The waitlist screen's photo in the app (PUT /app-config/waitlist). Empty = the bundled photo.
  const [photoUrl, setPhotoUrl] = useState("");
  const [photoDraft, setPhotoDraft] = useState("");
  const [savingPhoto, setSavingPhoto] = useState(false);
  // Until the current value loads, the form stays off so a failed load can't overwrite it unseen.
  const [photoLoaded, setPhotoLoaded] = useState(false);
  const [photoLoadFailed, setPhotoLoadFailed] = useState(false);
  const [photoBroken, setPhotoBroken] = useState(false);

  useEffect(() => {
    api.get("/app-config").then((res) => {
      // The server answers a failed read with defaults plus `error`: that isn't the real value.
      if (res.data?.error) throw new Error(res.data.error);
      const url = res.data?.waitlist?.photoUrl || "";
      setPhotoUrl(url);
      setPhotoDraft(url);
      setPhotoLoaded(true);
    }).catch(() => setPhotoLoadFailed(true));
  }, []);

  const savePhoto = async (next) => {
    setSavingPhoto(true);
    try {
      if (next.trim() && !/^https:\/\//i.test(next.trim())) {
        alert("Use an https link.");
        return;
      }
      if (next.trim() && !(await opensAsImage(next.trim()))) {
        alert("That link doesn't open as an image here, so it wasn't saved. Use a direct link to a .jpg or .png.");
        return;
      }
      const res = await api.put("/app-config/waitlist", { photoUrl: next.trim() });
      const url = res.data?.waitlist?.photoUrl || "";
      setPhotoUrl(url);
      setPhotoDraft(url);
      setPhotoBroken(false);
    } catch (err) {
      alert(err?.response?.data?.message || "Could not save the photo.");
    } finally {
      setSavingPhoto(false);
    }
  };

  const load = async (which = status) => {
    const loadId = ++latestLoad.current;
    try {
      const res = await api.get("/users/admin/join-requests", { params: { status: which } });
      // A slower response for a tab the admin already left must not overwrite the current one.
      if (loadId !== latestLoad.current) return;
      setData(res.data?.data || []);
      setCounts(res.data?.counts || { pending: 0, approved: 0, declined: 0 });
    } catch (err) {
      console.error("Error loading join requests ==>", err);
      alert("Could not load join requests.");
    }
  };

  useEffect(() => {
    load(status);
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const review = async (r, decision) => {
    const name = `${r.firstName || ""} ${r.lastName || ""}`.trim() || "this person";
    const question = decision === "approve"
      ? `Approve ${name}? They get in right away and get a "You're in" notification.`
      : `Decline ${name}? They stay locked out of the app.`;
    if (!window.confirm(question)) return;
    setBusyId(r._id);
    try {
      await api.post(`/users/admin/join-requests/${r._id}/${decision}`);
      await load();
      if (typeof refreshCounts === "function") refreshCounts();
    } catch (err) {
      alert(err?.response?.data?.message || "Could not save the decision.");
      load();
    } finally {
      setBusyId(null);
    }
  };

  const filtered = useMemo(() => {
    return data.filter((r) => matchesSearch(r, searchTerm));
  }, [data, searchTerm]);

  const priorityWaiting = status === "pending" ? data.filter(isPriority).length : 0;
  const pageRows = filtered.slice((currentPage - 1) * RECORDS, currentPage * RECORDS);

  const formatDate = (d) => (d ? format(new Date(d), "MMM d, yyyy") : "-");
  const link = (href, text) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="join-requests__link">
      {text}
    </a>
  );

  const toRow = (r) => {
    const referral = referralLine(r);
    const links = socialLinks(r.application);
    return {
      Photo: (
        <img className="join-requests__photo" src={r.photo_url || profileDefault} alt={r.firstName || "Applicant"} />
      ),
      Name: (
        <div>
          <div className="join-requests__name">
            {`${r.firstName || ""} ${r.lastName || ""}`.trim() || "No name"}
            {r.banned && <span className="join-requests__banned"> (banned)</span>}
          </div>
          {referral && <div className="join-requests__invited">{referral}</div>}
        </div>
      ),
      Request: !r.application
        ? <span className="join-requests__muted">Not sent yet</span>
        : r.application.about
          ? <div className="join-requests__about">{r.application.about}</div>
          : <span className="join-requests__muted">Sent on the old form</span>,
      Account: links.length
        ? <div className="join-requests__links">{links.map((l) => <span key={l.href}>{link(l.href, l.label)}</span>)}</div>
        : "-",
      Verified: <span className={r.verified ? "join-requests__verified" : "join-requests__muted"}>{verificationLabel(r)}</span>,
      Age: r.age ?? "-",
      City: r.city ? r.city.toUpperCase() : "-",
      Requested: r.application ? formatDate(r.application.submitted_at) : "-",
      Actions: (
        <div className="join-requests__actions">
          {r.membership_status !== "approved" && (
            <button className="join-requests__btn join-requests__btn--approve" disabled={busyId === r._id} onClick={() => review(r, "approve")}>
              Approve
            </button>
          )}
          {r.membership_status === "pending" && (
            <button className="join-requests__btn join-requests__btn--decline" disabled={busyId === r._id} onClick={() => review(r, "decline")}>
              Decline
            </button>
          )}
        </div>
      ),
      id: r._id,
    };
  };

  const fetchAllData = async () =>
    filtered.map((r) => ({
      Name: `${r.firstName || ""} ${r.lastName || ""}`.trim(),
      "Referred By": referralLine(r) || "",
      Request: r.application?.about || "",
      Email: r.email || "",
      Phone: r.phone || "",
      Age: r.age ?? "",
      Gender: r.gender || "",
      City: r.city || "",
      Instagram: r.application?.instagram ? `@${r.application.instagram}` : "",
      TikTok: r.application?.tiktok ? `@${r.application.tiktok}` : "",
      LinkedIn: r.application?.linkedin || "",
      Verified: verificationLabel(r),
      Requested: r.application ? formatDate(r.application.submitted_at) : "",
      "Signed Up": formatDate(r.created_at),
      Status: r.membership_status,
      id: r._id,
    }));

  const columns = ["Photo", "Name", "Request", "Account", "Verified", "Age", "City", "Requested", "Actions"];

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content">
          <section className="users join-requests">
            <div className="join-requests__tabs">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  className={`join-requests__tab${status === t.key ? " join-requests__tab--active" : ""}`}
                  onClick={() => setStatus(t.key)}
                >
                  {t.label} ({counts[t.key] ?? 0})
                </button>
              ))}
            </div>
            {priorityWaiting > 0 && (
              <div className="join-requests__notice">
                {priorityWaiting === 1 ? "1 invited, referred or verified request is waiting." : `${priorityWaiting} invited, referred or verified requests are waiting.`} They're at the top.
              </div>
            )}
            <div className="join-requests__photo-setting">
              <img
                className="join-requests__photo-preview"
                src={photoUrl || "/waitlist-default.jpg"}
                alt="Waitlist screen"
                onLoad={() => setPhotoBroken(false)}
                onError={() => setPhotoBroken(true)}
              />
              <div className="join-requests__photo-form">
                <div className="join-requests__photo-label">Waitlist screen photo</div>
                <div className="join-requests__muted">An https image link. Empty uses the photo built into the app.</div>
                {photoLoadFailed && <div className="join-requests__warning">Couldn't load the current photo setting. Reload to change it.</div>}
                {photoBroken && <div className="join-requests__warning">The current photo doesn't open as an image in this browser.</div>}
                <div className="join-requests__photo-row">
                  <input
                    className="join-requests__photo-input"
                    value={photoDraft}
                    placeholder="https://..."
                    disabled={!photoLoaded}
                    onChange={(e) => setPhotoDraft(e.target.value)}
                  />
                  <button className="join-requests__btn join-requests__btn--approve" disabled={!photoLoaded || savingPhoto || photoDraft.trim() === photoUrl} onClick={() => savePhoto(photoDraft)}>
                    Save
                  </button>
                  {photoUrl && (
                    <button className="join-requests__btn join-requests__btn--decline" disabled={savingPhoto} onClick={() => savePhoto("")}>
                      Use default
                    </button>
                  )}
                </div>
              </div>
            </div>
            <TableColumn
              key={status}
              title="Join Requests"
              hasHeader={true}
              columns={columns}
              data={pageRows.map(toRow)}
              sortableColumns={[]}
              hasExport={true}
              hasSearch={true}
              totalPages={filtered.length}
              currentPage={currentPage}
              onPageChange={(page) => setCurrentPage(page)}
              records={RECORDS}
              inputSearchProps={{ value: searchTerm, onChange: (e) => { setSearchTerm(e.target.value); setCurrentPage(1); } }}
              fetchAllData={fetchAllData}
              onSort={() => {}}
            />
          </section>
        </article>
      </div>
    </main>
  );
};

export default JoinRequests;
